import { Check, ArrowUpDown } from '../../icons';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { WorkspacePopover, PopoverItem, PopoverSep, PopoverLabel } from './WorkspacePopover';

/**
 * 정렬 메뉴 (2026-08-14) — 사용자 요구:
 *   "정렬은 이름순, 유형, 수정일자, 파일크기, 오름차순, 내림차순으로 있다"
 *
 * 축(4)과 방향(2)은 **직교**다 — 8개를 나열하지 않고 두 묶음으로 나눈다. 8개를 한 줄씩
 * 늘어놓으면 "이름순 오름차순"과 "이름순 내림차순" 중 지금 무엇인지 읽기 어렵다.
 *
 * ⚠ 판정은 서버가 한다. 목록이 커서 페이지네이션이라 클라이언트에서 정렬하면 페이지
 *   경계에서 순서가 깨진다(2페이지가 1페이지보다 앞 이름을 가져올 수 있다).
 */

/**
 * 2026-08-14 (2차) — `shared`(공유일) 추가. **공유됨 화면의 날짜 열 전용**이라 이 메뉴에는
 * 넣지 않는다(다른 화면에서는 고를 수 없는 축이 목록에 있으면 눌러도 안 바뀌는 항목이 된다).
 */
export type SortField = 'name' | 'type' | 'updated' | 'size' | 'shared';
export type SortOrder = 'asc' | 'desc';

const FIELDS: ReadonlyArray<{ field: SortField; labelKey: WorkspaceI18nKey }> = [
  { field: 'name', labelKey: 'workspace.sort.name' },
  { field: 'type', labelKey: 'workspace.sort.type' },
  { field: 'updated', labelKey: 'workspace.sort.updated' },
  { field: 'size', labelKey: 'workspace.sort.size' },
];

interface Props {
  anchor: HTMLElement;
  field: SortField;
  order: SortOrder;
  onChange: (next: { field: SortField; order: SortOrder }) => void;
  onClose: () => void;
}

export function WorkspaceSortMenu({ anchor, field, order, onChange, onClose }: Props) {
  return (
    <WorkspacePopover
      anchor={anchor}
      onClose={onClose}
      align="right"
      width={200}
      ariaLabel={tWorkspace('workspace.sort.aria')}
    >
      <PopoverLabel>{tWorkspace('workspace.sort.by')}</PopoverLabel>
      {FIELDS.map((f) => (
        <PopoverItem
          key={f.field}
          icon={Check}
          // 아이콘이 체크 표시라 안 고른 줄에서는 자리만 남긴다(라벨이 안 흔들리게).
          radio
          label={tWorkspace(f.labelKey)}
          checked={field === f.field}
          onClick={() => { onChange({ field: f.field, order }); onClose(); }}
        />
      ))}

      <PopoverSep />
      <PopoverLabel>{tWorkspace('workspace.sort.direction')}</PopoverLabel>
      <PopoverItem
        icon={ArrowUpDown}
        radio
        label={tWorkspace('workspace.sort.asc')}
        checked={order === 'asc'}
        onClick={() => { onChange({ field, order: 'asc' }); onClose(); }}
      />
      <PopoverItem
        icon={ArrowUpDown}
        radio
        label={tWorkspace('workspace.sort.desc')}
        checked={order === 'desc'}
        onClick={() => { onChange({ field, order: 'desc' }); onClose(); }}
      />
    </WorkspacePopover>
  );
}
